import RAPIER from '@dimforge/rapier3d-compat';
import { UNIT_HALF_HEIGHT, UNIT_RADIUS } from './constants';
import type { Triangles } from './glb';
import type { MapColliders } from './map';
import {
  dot,
  normalizeOrZero,
  scale,
  add,
  sub,
  length,
  v3,
  type V3,
  ZERO,
} from './vec';

let ready: Promise<void> | undefined;
/** Loads Rapier's WASM. Every `CollisionWorld` needs this to have resolved first. */
export const initPhysics = (): Promise<void> => (ready ??= RAPIER.init());

/** Contact normals within ~2.6° of the touched face's normal are treated as face contacts. */
const FACE_CONTACT_COS = 0.999;
/** How far off the surface the face-normal probe ray starts. */
const FACE_PROBE = 0.05;
const IDENTITY = { x: 0, y: 0, z: 0, w: 1 };

/** First contact of a capsule cast. */
export interface CapsuleHit {
  /** Fraction of the cast travelled before stopping `skin` away from the surface. */
  fraction: number;
  /** Contact normal, pointing out of the surface toward the capsule. */
  normal: V3;
  /** Normal of the touched face, oriented toward the capsule. Doesn't tilt on edges. */
  surfaceNormal: V3;
  /** True when `normal` is the touched face's exact normal; false on edges and vertices. */
  onFace: boolean;
  point: V3;
  collider: number;
}

export interface RayHit {
  distance: number;
  /** Normal of the face hit, oriented against the ray. */
  normal: V3;
  collider: number;
}

export interface Penetration {
  depth: number;
  /** Direction that pushes the capsule out of the collider. */
  normal: V3;
  collider: number;
}

/** Static arena geometry as one Rapier query surface for the character capsule. */
export class CollisionWorld {
  private world: RAPIER.World;
  private capsule = new RAPIER.Capsule(
    UNIT_HALF_HEIGHT - UNIT_RADIUS,
    UNIT_RADIUS,
  );

  constructor(colliders: MapColliders) {
    this.world = new RAPIER.World(ZERO);
    if (colliders.mesh.indices.length > 0) {
      this.world.createCollider(
        RAPIER.ColliderDesc.trimesh(
          colliders.mesh.positions,
          colliders.mesh.indices,
        ),
      );
    }
    for (const points of colliders.hulls) {
      const desc = RAPIER.ColliderDesc.convexHull(points);
      if (desc) this.world.createCollider(desc);
    }
    // Queries only see colliders once the broad phase has run.
    this.world.step();
  }

  static fromMeshes(meshes: Triangles[]): CollisionWorld {
    const positions: number[] = [];
    const indices: number[] = [];
    for (const mesh of meshes) {
      const base = positions.length / 3;
      positions.push(...mesh.positions);
      for (const i of mesh.indices) indices.push(base + i);
    }
    return new CollisionWorld({
      mesh: {
        positions: new Float32Array(positions),
        indices: new Uint32Array(indices),
      },
      hulls: [],
    });
  }

  free(): void {
    this.world.free();
  }

  /**
   * Sweeps the unit capsule from `center` along `delta`, stopping `skin` short of the first
   * surface in the way. A surface the capsule already touches only blocks motion heading into it.
   */
  castCapsule(center: V3, delta: V3, skin: number): CapsuleHit | null {
    const hit = this.world.castShape(
      center,
      IDENTITY,
      delta,
      this.capsule,
      skin,
      1,
      false,
    );
    if (!hit) return null;
    // Rapier's world-level cast reports the hit collider as shape 1. Colliders sit at the
    // identity, so its local frame is the world frame.
    const contact = normalizeOrZero(hit.normal1);
    const point = v3(hit.witness1.x, hit.witness1.y, hit.witness1.z);
    const face = this.faceNormalAt(point, contact);
    const surface = face
      ? dot(face, contact) < 0
        ? scale(face, -1)
        : face
      : contact;
    // The contact normal wobbles by a few tenths of a degree on face contacts, which is enough to
    // bend slides off course; snap those to the exact face normal.
    const onFace = face !== null && dot(surface, contact) > FACE_CONTACT_COS;
    return {
      fraction: hit.time_of_impact,
      normal: onFace ? surface : contact,
      surfaceNormal: surface,
      onFace,
      point,
      collider: hit.collider.handle,
    };
  }

  /** The deepest overlap between the unit capsule at `center` and any collider. */
  deepestPenetration(center: V3): Penetration | null {
    let deepest: Penetration | null = null;
    this.world.intersectionsWithShape(
      center,
      IDENTITY,
      this.capsule,
      (collider) => {
        const contact = collider.contactShape(
          this.capsule,
          center,
          IDENTITY,
          0,
        );
        if (
          contact &&
          contact.distance < 0 &&
          (!deepest || -contact.distance > deepest.depth)
        ) {
          deepest = {
            depth: -contact.distance,
            normal: normalizeOrZero(contact.normal1),
            collider: collider.handle,
          };
        }
        return true;
      },
    );
    return deepest;
  }

  /** Casts a ray from `origin` along the unit vector `dir`. */
  castRay(origin: V3, dir: V3, maxDistance: number): RayHit | null {
    const hit = this.world.castRayAndGetNormal(
      new RAPIER.Ray(origin, dir),
      maxDistance,
      true,
    );
    if (!hit) return null;
    const normal = v3(hit.normal.x, hit.normal.y, hit.normal.z);
    if (normal.x === 0 && normal.y === 0 && normal.z === 0) return null;
    return {
      distance: hit.timeOfImpact,
      normal: dot(normal, dir) > 0 ? scale(normal, -1) : normal,
      collider: hit.collider.handle,
    };
  }

  /** Whether nothing solid lies on the segment from `a` to `b`. */
  lineOfSight(a: V3, b: V3): boolean {
    const span = sub(b, a);
    const dist = length(span);
    if (dist < 1e-6) return true;
    return this.castRay(a, scale(span, 1 / dist), dist) === null;
  }

  /** The face under `point` (a contact on a surface whose contact normal is `contact`). */
  private faceNormalAt(point: V3, contact: V3): V3 | null {
    const from = add(point, scale(contact, FACE_PROBE));
    const hit = this.castRay(from, scale(contact, -1), 2 * FACE_PROBE);
    return hit ? hit.normal : null;
  }
}
